// ─── BRAND THEME — THE ONLY PLACE TO CHANGE THE APP'S COLORS ─────────────────
//
//   Change BRAND below and the whole app follows: buttons, sidebar, focus rings,
//   badges, spinners, gradients, avatars, the login screen, every tab.
//
//   How it works: every shade is DERIVED from BRAND, then published as CSS
//   variables (App.js injects THEME_CSS_VARS). Components use either
//     • JS constants from this file (inline styles), or
//     • Tailwind classes that read the variables, e.g. bg-[rgb(var(--brand-rgb))]
//       and bg-[rgb(var(--brand-rgb)/0.1)] for a 10% tint.
//   Never hardcode a hex color in a component — import from here instead.
// ─────────────────────────────────────────────────────────────────────────────

const BRAND = "#85011d";   // ← change this ONE value (3- or 6-digit hex)

// Optional second color for gradients (logo, avatars, client cards).
// Leave "" to auto-use a lighter shade of BRAND, or set e.g. "#E8C56A" for two-tone.
const BRAND_ACCENT_OVERRIDE = "";

// ─── Derivation helpers (no need to edit) ────────────────────────────────────
const normalizeHex = (h) => {
  let x = String(h).trim().replace("#", "");
  if (x.length === 3) x = x.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(x)) throw new Error(`theme.js: invalid color "${h}"`);
  return `#${x.toUpperCase()}`;
};
const toRgb = (hex) => {
  const n = parseInt(normalizeHex(hex).slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (rgb) =>
  "#" + rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("").toUpperCase();
// amount > 0 mixes toward white, amount < 0 mixes toward black
const shade = (hex, amount) => {
  const target = amount > 0 ? 255 : 0;
  const p = Math.abs(amount);
  return toHex(toRgb(hex).map((c) => c + (target - c) * p));
};
const channels = (hex) => toRgb(hex).join(" "); // "155 50 240" for rgb(var(--x) / a)

// ─── Derived palette ─────────────────────────────────────────────────────────
export const BRAND_HEX    = normalizeHex(BRAND);
export const BRAND_DARK   = shade(BRAND_HEX, -0.18);  // hover / pressed / text on light tint
export const BRAND_ACCENT = BRAND_ACCENT_OVERRIDE
  ? normalizeHex(BRAND_ACCENT_OVERRIDE)
  : shade(BRAND_HEX, 0.38);                           // gradient end

/** Brand color at any opacity, e.g. brandAlpha(0.15) → "rgba(155,50,240,0.15)". */
export const brandAlpha = (a) => `rgba(${toRgb(BRAND_HEX).join(",")},${a})`;

export const BRAND_LIGHT = brandAlpha(0.1);   // tint (badges, hover backgrounds)
export const BRAND_RING  = brandAlpha(0.28);  // focus glow / box-shadow

export const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND_HEX} 0%, ${BRAND_ACCENT} 100%)`;

// ─── CSS variables — injected once by App.js ─────────────────────────────────
export const THEME_CSS_VARS = `
  :root {
    --brand: ${BRAND_HEX};
    --brand-rgb: ${channels(BRAND_HEX)};
    --brand-dark: ${BRAND_DARK};
    --brand-dark-rgb: ${channels(BRAND_DARK)};
    --brand-accent: ${BRAND_ACCENT};
    --brand-accent-rgb: ${channels(BRAND_ACCENT)};
    --brand-light: ${BRAND_LIGHT};
    --brand-ring: ${BRAND_RING};
  }
`;

// ─── Pre-built Tailwind classes (read the variables, so they follow BRAND) ───
export const B = {
  bg:       "bg-[rgb(var(--brand-rgb))]",
  bgLight:  "bg-[rgb(var(--brand-rgb)/0.1)]",
  text:     "text-[rgb(var(--brand-rgb))]",
  border:   "border-[rgb(var(--brand-rgb))]",
  ring:     "ring-[rgb(var(--brand-rgb))]",
  hover:    "hover:bg-[rgb(var(--brand-rgb))]",
  gradient: "bg-gradient-to-br from-[rgb(var(--brand-rgb))] to-[rgb(var(--brand-accent-rgb))]",
};